import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, Upload, X, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CATEGORIES = ["Apparel", "Jewellery", "Handbag", "Footwear", "Accessories"];

function FormField({ label, name, type = "text", required = false, full = false, form, set, ...rest }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[10px] tracking-[0.32em] uppercase text-silver/70">{label}{required ? " *" : ""}</label>
      <input
        data-testid={`field-${name}`}
        type={type}
        required={required}
        value={form[name] ?? ""}
        onChange={(e) => set(name, e.target.value)}
        className="lux-input"
        {...rest}
      />
    </div>
  );
}

const EMPTY = {
  sku: "", name: "", category: "Apparel", tagline: "", description: "",
  price_inr: 0, price_usd: 0, image: "", images: [],
  fabric: "", care: "", materials: [], sizes: [], colors: [],
  stock: 0, low_stock_threshold: 5, available: true, hidden: false,
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function imgUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return BACKEND_URL + u;
}

export default function AdminProductForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = !!id;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!editing) return;
    api.get(`/admin/products`).then((r) => {
      const p = r.data.find((x) => x.id === id);
      if (p) setForm({ ...EMPTY, ...p });
      setLoading(false);
    });
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e, asMain = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (form.sku) fd.append("sku", form.sku);
    try {
      const r = await api.post("/admin/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url;
      if (asMain || !form.image) {
        set("image", url);
      }
      set("images", [...(form.images || []), url]);
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (url) => {
    set("images", form.images.filter((x) => x !== url));
    if (form.image === url) set("image", "");
  };

  const addSize = () => set("sizes", [...form.sizes, { label: "", in_stock: true }]);
  const updateSize = (i, k, v) => set("sizes", form.sizes.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const removeSize = (i) => set("sizes", form.sizes.filter((_, idx) => idx !== i));

  const addColor = () => set("colors", [...form.colors, { name: "", hex: "#000000", in_stock: true }]);
  const updateColor = (i, k, v) => set("colors", form.colors.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const removeColor = (i) => set("colors", form.colors.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image) { toast.error("Please upload at least one image."); return; }
    if (!form.sku.trim() || !form.name.trim()) { toast.error("SKU and name are required."); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        materials: typeof form.materials === "string" ? form.materials.split(",").map((m) => m.trim()).filter(Boolean) : form.materials,
        price_inr: Number(form.price_inr) || 0,
        price_usd: Number(form.price_usd) || 0,
        stock: Number(form.stock) || 0,
        low_stock_threshold: Number(form.low_stock_threshold) || 5,
      };
      if (editing) {
        await api.put(`/admin/products/${id}`, payload);
        toast.success("Product updated.");
      } else {
        await api.post("/admin/products", payload);
        toast.success("Product created.");
      }
      navigate("/atelier-admin/products");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (Array.isArray(d) ? d[0]?.msg : "Save failed."));
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-white/50">Loading…</div>;

  return (
    <form onSubmit={submit} className="space-y-10 max-w-5xl" data-testid="admin-product-form">
      <div className="flex items-center justify-between">
        <Link to="/atelier-admin/products" className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-white/60 hover:text-white">
          <ArrowLeft size={14} /> Back to products
        </Link>
      </div>

      <div>
        <div className="eyebrow text-silver/70 mb-2">{editing ? "Edit" : "Create"}</div>
        <h1 className="font-serif text-white text-3xl">{editing ? form.name || "Edit product" : "New product"}</h1>
      </div>

      {/* Images */}
      <section className="border border-white/8 bg-midnight p-6 space-y-4">
        <h2 className="eyebrow text-silver">Images</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {(form.images || []).map((u, i) => (
            <div key={i} className={`relative aspect-[3/4] border ${form.image === u ? "border-silver" : "border-white/10"} bg-midnight-panel`}>
              <img src={imgUrl(u)} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removeImage(u)}
                className="absolute top-1 right-1 w-7 h-7 bg-black/70 text-white flex items-center justify-center"
                aria-label="Remove"
              ><X size={12} /></button>
              {form.image !== u && (
                <button
                  type="button"
                  onClick={() => set("image", u)}
                  className="absolute bottom-1 left-1 right-1 bg-black/70 text-white text-[10px] tracking-[0.25em] uppercase py-1"
                >Set main</button>
              )}
              {form.image === u && (
                <span className="absolute bottom-1 left-1 right-1 bg-silver text-black text-[10px] tracking-[0.25em] uppercase py-1 text-center">Main</span>
              )}
            </div>
          ))}
          <label className="aspect-[3/4] border border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-silver text-white/60 hover:text-white">
            <Upload size={20} strokeWidth={1.2} />
            <span className="text-[10px] tracking-[0.28em] uppercase mt-2">{uploading ? "Uploading…" : "Upload"}</span>
            <input type="file" accept="image/*" className="hidden" data-testid="image-upload" onChange={(e) => handleUpload(e)} disabled={uploading} />
          </label>
        </div>
        <p className="text-[10px] tracking-[0.25em] uppercase text-white/40">JPG, PNG, WebP up to 10 MB</p>
      </section>

      {/* Basics */}
      <section className="border border-white/8 bg-midnight p-6">
        <h2 className="eyebrow text-silver mb-4">Basics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField label="Name" name="name" required form={form} set={set} />
          <FormField label="SKU / Design Code" name="sku" required form={form} set={set} />
          <div>
            <label className="text-[10px] tracking-[0.32em] uppercase text-silver/70">Category *</label>
            <select
              data-testid="field-category"
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className="lux-input bg-midnight-deep"
              required
            >
              {CATEGORIES.map((c) => <option key={c} value={c} className="bg-midnight-deep">{c}</option>)}
            </select>
          </div>
          <FormField label="Tagline" name="tagline" form={form} set={set} />
          <div className="md:col-span-2">
            <label className="text-[10px] tracking-[0.32em] uppercase text-silver/70">Description</label>
            <textarea
              data-testid="field-description"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={4}
              className="w-full bg-midnight-deep border border-white/10 text-white p-3 focus:border-silver outline-none"
            />
          </div>
        </div>
      </section>

      {/* Pricing & stock */}
      <section className="border border-white/8 bg-midnight p-6">
        <h2 className="eyebrow text-silver mb-4">Pricing & Inventory</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          <FormField label="Price · INR (₹)" name="price_inr" type="number" required min={0} form={form} set={set} />
          <FormField label="Price · USD ($)" name="price_usd" type="number" required min={0} step="0.01" form={form} set={set} />
          <FormField label="Stock quantity" name="stock" type="number" required min={0} form={form} set={set} />
          <FormField label="Low-stock threshold" name="low_stock_threshold" type="number" min={1} form={form} set={set} />
        </div>
        <div className="mt-4 flex gap-6 text-sm">
          <label className="flex items-center gap-2 text-white/80">
            <input type="checkbox" checked={form.available !== false} onChange={(e) => set("available", e.target.checked)} data-testid="field-available" /> Available for purchase
          </label>
          <label className="flex items-center gap-2 text-white/80">
            <input type="checkbox" checked={!!form.hidden} onChange={(e) => set("hidden", e.target.checked)} data-testid="field-hidden" /> Hide from store
          </label>
        </div>
      </section>

      {/* Specifications */}
      <section className="border border-white/8 bg-midnight p-6">
        <h2 className="eyebrow text-silver mb-4">Specifications</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <label className="text-[10px] tracking-[0.32em] uppercase text-silver/70">Fabric / Material details</label>
            <textarea
              data-testid="field-fabric"
              value={form.fabric}
              onChange={(e) => set("fabric", e.target.value)}
              rows={2}
              className="w-full bg-midnight-deep border border-white/10 text-white p-3 focus:border-silver outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] tracking-[0.32em] uppercase text-silver/70">Care instructions</label>
            <textarea
              data-testid="field-care"
              value={form.care}
              onChange={(e) => set("care", e.target.value)}
              rows={2}
              className="w-full bg-midnight-deep border border-white/10 text-white p-3 focus:border-silver outline-none"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] tracking-[0.32em] uppercase text-silver/70">Materials (comma-separated)</label>
            <input
              data-testid="field-materials"
              value={Array.isArray(form.materials) ? form.materials.join(", ") : (form.materials || "")}
              onChange={(e) => set("materials", e.target.value)}
              className="lux-input"
            />
          </div>
        </div>
      </section>

      {/* Sizes */}
      <section className="border border-white/8 bg-midnight p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="eyebrow text-silver">Sizes</h2>
          <button type="button" onClick={addSize} className="text-[10px] tracking-[0.28em] uppercase text-white/70 hover:text-white border border-white/15 px-3 py-1.5 inline-flex items-center gap-1" data-testid="add-size">
            <Plus size={12} /> Add size
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {form.sizes.map((s, i) => (
            <div key={i} className="flex items-center gap-3 border border-white/10 p-3">
              <input
                value={s.label}
                placeholder="Label (e.g. S / 38)"
                onChange={(e) => updateSize(i, "label", e.target.value)}
                className="lux-input flex-1"
              />
              <label className="text-[10px] tracking-[0.25em] uppercase text-white/70 flex items-center gap-1">
                <input type="checkbox" checked={s.in_stock} onChange={(e) => updateSize(i, "in_stock", e.target.checked)} />
                In stock
              </label>
              <button type="button" onClick={() => removeSize(i)} className="text-white/50 hover:text-white"><X size={14} /></button>
            </div>
          ))}
        </div>
      </section>

      {/* Colors */}
      <section className="border border-white/8 bg-midnight p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="eyebrow text-silver">Colors</h2>
          <button type="button" onClick={addColor} className="text-[10px] tracking-[0.28em] uppercase text-white/70 hover:text-white border border-white/15 px-3 py-1.5 inline-flex items-center gap-1" data-testid="add-color">
            <Plus size={12} /> Add color
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {form.colors.map((c, i) => (
            <div key={i} className="flex items-center gap-3 border border-white/10 p-3">
              <input
                value={c.name}
                placeholder="Name"
                onChange={(e) => updateColor(i, "name", e.target.value)}
                className="lux-input flex-1"
              />
              <input
                type="color"
                value={c.hex}
                onChange={(e) => updateColor(i, "hex", e.target.value)}
                className="w-10 h-10 border border-white/15 cursor-pointer"
              />
              <label className="text-[10px] tracking-[0.25em] uppercase text-white/70 flex items-center gap-1">
                <input type="checkbox" checked={c.in_stock} onChange={(e) => updateColor(i, "in_stock", e.target.checked)} />
                In stock
              </label>
              <button type="button" onClick={() => removeColor(i)} className="text-white/50 hover:text-white"><X size={14} /></button>
            </div>
          ))}
        </div>
      </section>

      <div className="flex gap-3">
        <button type="submit" disabled={saving} data-testid="save-product" className="bg-white text-black px-8 py-3.5 text-[11px] tracking-[0.32em] uppercase hover:bg-silver disabled:opacity-50">
          {saving ? "Saving…" : editing ? "Save changes" : "Create product"}
        </button>
        <Link to="/atelier-admin/products" className="border border-white/20 px-8 py-3.5 text-[11px] tracking-[0.32em] uppercase text-white hover:border-white inline-flex items-center">
          Cancel
        </Link>
      </div>
    </form>
  );
}
